"use client"

import { cn } from "@/lib/utils"
import Image from "next/image"
import React, { useEffect } from "react"

interface InitialLoadingScreenProps {
  className?: string
  children?: React.ReactNode
}

/**
 * Full-screen loading component with pulsing logo animation
 * Used for initial page load transitions and navigation loading
 * Supports children for additional UI elements (e.g., progress bar)
 */
const InitialLoadingScreen: React.FC<InitialLoadingScreenProps> = ({
  className = "",
  children,
}) => {
  const LOGO_FILE = process.env.NEXT_PUBLIC_APP_LOGO_FILE || "/sustainability-hub.svg"
  const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || ""

  // Construct image source safely
  // Ensure LOGO_FILE starts with / for relative paths
  const logoPath = LOGO_FILE.startsWith("/") ? LOGO_FILE : `/${LOGO_FILE}`

  // Only use BASE_URL if it's a valid URL (starts with http:// or https://)
  const imageSrc =
    BASE_URL &&
    BASE_URL.trim() !== "" &&
    (BASE_URL.startsWith("http://") || BASE_URL.startsWith("https://"))
      ? `${BASE_URL.replace(/\/$/, "")}${logoPath}`
      : logoPath

  useEffect(() => {
    // Force vertical scrollbar to appear to prevent layout shift
    document.body.style.overflowY = "scroll"

    return () => {
      // Reset overflow when component unmounts
      document.body.style.overflowY = ""
    }
  }, [])

  return (
    <div
      className={cn(
        "bg-background fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-500",
        className
      )}
      role="status"
      aria-label="Loading application"
    >
      <Image
        src={imageSrc}
        alt="UIC, the International Union of Railways (symbol)"
        width={300}
        height={300}
        className="animate-pulse transition-all duration-500"
        priority
        unoptimized={imageSrc.endsWith(".svg")}
      />
      {children}
    </div>
  )
}

InitialLoadingScreen.displayName = "InitialLoadingScreen"

export default InitialLoadingScreen
